import { useState, useRef } from "react";

import {
    debounce,
    Autocomplete,
    TextField,
    SxProps,
    Theme,
    TextFieldVariants,
} from "@mui/material";

import { useSearchSitesQuery } from "services/streamingApi/data.api";

import { siteFullname } from "utils/siteFullname";

import SiteSearchResult from "types/SiteSearchResult";

export interface SitesAutocompleteProps {
    minSearchQueryCharacterLength?: number;
    sx?: SxProps<Theme>;
    onChange: (value: SiteSearchResult | null) => void;
    hideSelectedValue?: boolean;
    variant?: TextFieldVariants;
    label?: string;
    required?: boolean;

    error?: boolean;
    helperText?: React.ReactNode;
    disableClearable?: boolean;

    margin?: "dense" | "normal" | "none";
}

export function SitesAutocomplete(props: SitesAutocompleteProps) {
    // component state
    const [selectedValue, setSelectedValue] = useState<SiteSearchResult | null>(
        null,
    );
    const [userInput, setUserInput] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const debouncedSearchInput = useRef(debounce(setSearchInput, 500));

    // hooks
    const { data, isLoading } = useSearchSitesQuery(searchInput, {
        skip:
            !searchInput ||
            (!!props.minSearchQueryCharacterLength &&
                searchInput.length < props.minSearchQueryCharacterLength),
    });

    // component
    return (
        <Autocomplete
            size="small"
            autoComplete
            filterSelectedOptions
            fullWidth
            includeInputInList
            loading={isLoading}
            getOptionLabel={(site: SiteSearchResult) => siteFullname(site)}
            filterOptions={(x) => x}
            options={data || []}
            inputValue={userInput}
            onInputChange={(event, newInputValue, reason) => {
                if (reason !== "reset") {
                    setUserInput(newInputValue);
                    debouncedSearchInput.current(newInputValue);
                }
            }}
            value={props.hideSelectedValue ? null : selectedValue}
            onChange={(event, value) => {
                props.onChange(value);

                setSelectedValue(value);

                if (props.hideSelectedValue) {
                    // clear user input
                    setUserInput("");
                }
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder="Zoek een club..."
                    variant={props.variant ?? "standard"}
                    label={props.label}
                    required={props.required}
                    error={props.error}
                    helperText={props.helperText}
                    margin={props.margin}
                />
            )}
            sx={[...(Array.isArray(props.sx) ? props.sx : [props.sx])]}
            disableClearable={props.disableClearable}
        />
    );
}
